/**
 * Requests sent to the Digital Council (E4.7, #1293) — list at a glance,
 * detail on click.
 *
 * The landing is a flat ticket table (decided 2026-09-03): reference, subject,
 * who, when, status — scannable the way the app's own List View is. Clicking a
 * row opens a **full detail view**, not a drawer: everything about the request
 * laid out as copyable text, because the Digital Council's next step is
 * usually to carry it into their own systems — and a Copy button hands them
 * the whole record in one gesture.
 *
 * Two things the ticket shape buys:
 * - **A reference.** `DC-0101` gives the other council a handle to cite —
 *   "about DC-0102" works in an email. A row without a name can only be
 *   pointed at.
 * - **A status.** Stored, and set by anyone who can see the page (provisional
 *   values in services/digital_council.DSAI_STATUSES, owner 2026-10-05).
 *
 * Still written for a reader **outside this application**: no focus-area
 * codes, no stage names, none of our vocabulary.
 */

import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  Button,
  MessageBar,
  MessageBarBody,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Title2,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from '@fluentui/react-components'
import { useDigitalCouncilQueue, type RoutedRequest } from '../hooks/useDigitalCouncil'
import { DsaiAssignment } from '../components/DsaiAssignment'
import { DsaiDiscussion } from '../components/DsaiDiscussion'
import { activatable } from '../lib/activatable'
import { apiErrorMessage } from '../lib/apiError'
import { useAuth } from '../context/AuthContext'
import { useEmailArrival } from '../hooks/useEmailArrival'

const useStyles = makeStyles({
  page: {
    padding: tokens.spacingVerticalXL,
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '1120px',
  },
  head: { marginBottom: tokens.spacingVerticalL },
  lede: {
    color: tokens.colorNeutralForeground3,
    marginTop: tokens.spacingVerticalXS,
    maxWidth: '68ch',
  },
  card: {
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    overflowX: 'auto',
  },
  table: { tableLayout: 'fixed', minWidth: '860px' },
  colRef: { width: '10%' },
  colSubject: { width: '38%' },
  colFrom: { width: '20%' },
  colReceived: { width: '11%' },
  colStatus: { width: '21%' },

  row: {
    cursor: 'pointer',
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  cellTop: { verticalAlign: 'top' },
  ref: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    whiteSpace: 'nowrap',
  },
  title: { fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1 },
  sub: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    marginTop: '2px',
  },
  num: { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
  chip: {
    display: 'inline-block',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    borderRadius: '10px',
    ...shorthands.padding('2px', tokens.spacingHorizontalM),
    whiteSpace: 'nowrap',
  },
  chipNew: {
    backgroundColor: tokens.colorPaletteGreenBackground1,
    color: tokens.colorPaletteGreenForeground2,
  },
  chipWaiting: {
    backgroundColor: tokens.colorPaletteMarigoldBackground1,
    color: tokens.colorPaletteMarigoldForeground2,
  },

  /* Detail view — a full page-within-the-page, built for reading and copying. */
  detail: {
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.padding(tokens.spacingVerticalXL, tokens.spacingHorizontalXXL),
    maxWidth: '860px',
  },
  backRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    columnGap: tokens.spacingHorizontalL,
    marginBottom: tokens.spacingVerticalM,
  },
  backLink: {
    color: tokens.colorBrandForeground1,
    cursor: 'pointer',
    backgroundColor: 'transparent',
    ...shorthands.border('0'),
    ...shorthands.padding('0'),
    fontSize: tokens.fontSizeBase300,
    fontFamily: 'inherit',
  },
  detailRef: {
    fontFamily: tokens.fontFamilyMonospace,
    color: tokens.colorNeutralForeground3,
    marginRight: tokens.spacingHorizontalM,
  },
  detailTitle: { marginTop: '4px', marginBottom: '2px', fontSize: tokens.fontSizeBase600 },
  detailMeta: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase300 },
  sectionLabel: {
    display: 'block',
    fontSize: tokens.fontSizeBase100,
    letterSpacing: '.08em',
    textTransform: 'uppercase',
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightSemibold,
    marginTop: tokens.spacingVerticalL,
    marginBottom: '4px',
  },
  bodyText: {
    color: tokens.colorNeutralForeground1,
    maxWidth: '72ch',
    whiteSpace: 'pre-wrap',
  },
  why: {
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
    backgroundColor: tokens.colorBrandBackground2,
    borderLeftWidth: '3px',
    borderLeftStyle: 'solid',
    borderLeftColor: tokens.colorBrandStroke1,
    borderRadius: `0 ${tokens.borderRadiusSmall} ${tokens.borderRadiusSmall} 0`,
    maxWidth: '72ch',
  },
  direct: { fontStyle: 'italic', color: tokens.colorNeutralForeground2 },
  detailFoot: {
    marginTop: tokens.spacingVerticalL,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  openLink: { color: tokens.colorBrandForeground1 },
  empty: {
    color: tokens.colorNeutralForeground3,
    ...shorthands.padding(tokens.spacingVerticalXXXL, tokens.spacingHorizontalL),
    textAlign: 'center',
  },
})

// Every routing records its reason now — a person's is required, and the
// classifier's is written for it at submission. This used to read "The
// requestor sent this to you directly from the intake form", which stopped
// being possible on 2026-09-11 (the server routes every submission) and was
// what the Digital Council read on every classifier-routed request (#1362).
const NO_REASON_RECORDED = 'No reason was recorded when this was sent to you.'

function fmtDate(iso?: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

function isNew(item: RoutedRequest): boolean {
  return (item.dsai_status ?? 'new') === 'new'
}

function statusLabel(item: RoutedRequest): string {
  return item.dsai_status_label ?? 'New'
}

type SortKey = 'ref' | 'subject' | 'from' | 'received' | 'status'

const SORT_VALUE: Record<SortKey, (item: RoutedRequest) => string | number> = {
  ref: (i) => i.demand_id,
  subject: (i) => i.title.toLowerCase(),
  from: (i) => (i.submitter_name ?? '').toLowerCase(),
  received: (i) => (i.routed_at ? new Date(i.routed_at).getTime() : 0),
  status: (i) => statusLabel(i).toLowerCase(),
}

/** The handle the other council cites — "about DC-0102" works in an email or
 *  a meeting; a row without a name can only be pointed at. */
function ticketRef(item: RoutedRequest): string {
  return `DC-${String(item.demand_id).padStart(4, '0')}`
}

/** The whole record as plain text — what "carry it into our own system"
 *  actually needs. One gesture instead of six swipe-selects. */
function asPlainText(item: RoutedRequest): string {
  return [
    `${ticketRef(item)} — ${item.title}`,
    `Requested by: ${item.submitter_name ?? 'Unknown requestor'}${
      item.department ? ` (${item.department})` : ''
    }`,
    item.submitted_at ? `Asked on: ${fmtDate(item.submitted_at)}` : null,
    item.routed_at
      ? `Sent to DSAI: ${fmtDate(item.routed_at)}${
          item.routed_by ? ` by ${item.routed_by}` : ''
        }`
      : null,
    '',
    'Request:',
    item.description,
    '',
    'Why it was sent:',
    item.reason ?? NO_REASON_RECORDED,
  ]
    .filter((line) => line !== null)
    .join('\n')
}

export function DigitalCouncilQueue() {
  const styles = useStyles()
  const query = useDigitalCouncilQueue()
  const { user } = useAuth()
  // The link back is for OUR side — reviewing a routing decision means opening
  // the demand. A Digital Council member is a plain `member` the demand
  // visibility layer would bounce, so for them the link would be a dead end
  // dressed as an affordance; the detail view already holds everything.
  const canOpenDemand =
    user?.role === 'council' || user?.role === 'admin' || user?.role === 'super_admin'

  // The open request lives in the URL (`?request=<demand id>`), so "this
  // one" can be sent to a colleague and the demand page can link straight to
  // what the Digital Council sees for it (#1376). It used to be component
  // state: every request shared one address.
  const [searchParams, setSearchParams] = useSearchParams()
  const requested = Number(searchParams.get('request'))
  const selectedId = Number.isInteger(requested) && requested > 0 ? requested : null
  const setSelectedId = (id: number | null) =>
    setSearchParams(id == null ? {} : { request: String(id) })
  const [copied, setCopied] = useState(false)

  const items = useMemo(() => query.data?.items ?? [], [query.data])
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({
    key: 'received',
    dir: 'desc',
  })
  const sorted = useMemo(() => {
    const value = SORT_VALUE[sort.key]
    const sign = sort.dir === 'asc' ? 1 : -1
    return [...items].sort((a, b) => {
      const x = value(a)
      const y = value(b)
      return x < y ? -sign : x > y ? sign : 0
    })
  }, [items, sort])
  const sortBy = (key: SortKey) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }))
  const header = (key: SortKey) => ({
    sortDirection:
      sort.key === key ? (sort.dir === 'asc' ? ('ascending' as const) : ('descending' as const)) : undefined,
    onClick: () => sortBy(key),
    'data-testid': `dc-sort-${key}`,
  })
  const selected = useMemo(
    () => (selectedId != null ? (items.find((i) => i.demand_id === selectedId) ?? null) : null),
    [items, selectedId],
  )
  useEmailArrival(selected?.demand_id ?? null, searchParams.get('src'))

  async function copyDetails(item: RoutedRequest) {
    try {
      await navigator.clipboard.writeText(asPlainText(item))
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard access can be denied; the text is on screen and selectable,
      // so the button failing quietly costs a swipe, not the content.
    }
  }

  if (query.isLoading) {
    return (
      <div className={styles.page}>
        <Spinner label="Loading…" />
      </div>
    )
  }

  if (query.isError) {
    return (
      <div className={styles.page}>
        <MessageBar intent="error">
          <MessageBarBody>Failed to load. {apiErrorMessage(query.error)}</MessageBarBody>
        </MessageBar>
      </div>
    )
  }

  /* ── the detail view: one request, everything copyable ── */
  if (selected) {
    return (
      <div className={styles.page}>
        <div className={styles.detail} data-testid={`dc-detail-${selected.demand_id}`}>
          <div className={styles.backRow}>
            <button
              type="button"
              className={styles.backLink}
              onClick={() => setSelectedId(null)}
              data-testid="dc-back"
            >
              ← All requests
            </button>
            <Button size="small" onClick={() => copyDetails(selected)} data-testid="dc-copy">
              {copied ? 'Copied ✓' : 'Copy details'}
            </Button>
          </div>

          <div>
            <span className={styles.detailRef}>{ticketRef(selected)}</span>
            <span
              className={mergeClasses(
                styles.chip,
                isNew(selected) ? styles.chipNew : styles.chipWaiting,
              )}
            >
              {statusLabel(selected)}
            </span>
          </div>
          <h2 className={styles.detailTitle}>{selected.title}</h2>
          <div className={styles.detailMeta}>
            {selected.submitter_name ?? 'Unknown requestor'}
            {selected.department ? ` · ${selected.department}` : ''}
            {selected.submitted_at ? ` · asked on ${fmtDate(selected.submitted_at)}` : ''}
            {/* The one thing the receiving organisation needs in order to act
                and could not get from this page: a way to reach the person. */}
            {selected.submitter_email && (
              <>
                {' · '}
                <a
                  href={`mailto:${selected.submitter_email}`}
                  className={styles.openLink}
                  data-testid={`dc-email-${selected.demand_id}`}
                >
                  {selected.submitter_email}
                </a>
              </>
            )}
          </div>

          <span className={styles.sectionLabel}>The request, in the requestor’s words</span>
          <div className={styles.bodyText}>{selected.description}</div>

          <span className={styles.sectionLabel}>Why we sent it to you</span>
          <div className={styles.why} data-testid={`dc-reason-${selected.demand_id}`}>
            {selected.reason ?? (
              <span className={styles.direct}>{NO_REASON_RECORDED}</span>
            )}
          </div>

          <span className={styles.sectionLabel}>Who in DSAI has it</span>
          <DsaiAssignment
            demandId={selected.demand_id}
            cluster={selected.dsai_cluster}
            clusterLabel={selected.dsai_cluster_label}
            caretakerId={selected.caretaker_id}
            caretakerName={selected.caretaker_name}
            canAssign={!!selected.can_assign}
          />

          <span className={styles.sectionLabel}>Status and comments</span>
          <DsaiDiscussion
            demandId={selected.demand_id}
            status={selected.dsai_status ?? 'new'}
          />

          <div className={styles.detailFoot}>
            {selected.routed_at ? `Sent to you ${fmtDate(selected.routed_at)}` : ''}
            {selected.routed_by ? ` by ${selected.routed_by}` : ''}
            {canOpenDemand && (
              <>
                {selected.routed_at || selected.routed_by ? ' · ' : ''}
                <Link
                  to={`/demand/${selected.demand_id}`}
                  className={styles.openLink}
                  data-testid={`dc-open-${selected.demand_id}`}
                >
                  See details in DACcelerate
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    )
  }

  /* ── the landing: at a glance ── */
  return (
    <div className={styles.page}>
      <div className={styles.head}>
        {/* Two audiences, two headings. "Sent to you" is only true for the
            Digital Council's own people; our council is here to review what
            was routed, and a heading addressed to someone else reads as being
            on the wrong page. */}
        <Title2>{canOpenDemand ? 'Routed to DSAI' : 'Requests sent to you'}</Title2>
        <div className={styles.lede}>
          {canOpenDemand
            ? 'Not DAC matters: why, and who in DSAI has it. Bring a wrong one back from the demand.'
            : 'Requests we think are yours. Open one for the details and why.'}
        </div>
      </div>

      {items.length === 0 ? (
        <div className={styles.empty} data-testid="dc-queue-empty">
          Nothing has been sent your way.
        </div>
      ) : (
        <div className={styles.card}>
          <Table
            size="small"
            sortable
            aria-label="Requests routed to DSAI"
            className={styles.table}
          >
            <TableHeader>
              <TableRow>
                <TableHeaderCell className={styles.colRef} {...header('ref')}>Ref</TableHeaderCell>
                <TableHeaderCell className={styles.colSubject} {...header('subject')}>Subject</TableHeaderCell>
                <TableHeaderCell className={styles.colFrom} {...header('from')}>Requested by</TableHeaderCell>
                <TableHeaderCell className={styles.colReceived} {...header('received')}>Received</TableHeaderCell>
                <TableHeaderCell className={styles.colStatus} {...header('status')}>Status</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((item) => (
                <TableRow
                  key={item.demand_id}
                  className={styles.row}
                  data-testid={`dc-item-${item.demand_id}`}
                  {...activatable(() => setSelectedId(item.demand_id))}
                >
                  <TableCell className={mergeClasses(styles.cellTop, styles.ref)}>
                    {ticketRef(item)}
                  </TableCell>
                  <TableCell className={styles.cellTop}>
                    <span className={styles.title}>{item.title}</span>
                    {/* Who in DSAI has it — or that nobody does yet, which is
                        the row that still needs someone on our side. */}
                    <div className={styles.sub} data-testid={`dc-item-cluster-${item.demand_id}`}>
                      {item.dsai_cluster_label ?? 'No DSAI cluster yet'}
                      {item.caretaker_name ? ` · ${item.caretaker_name}` : ''}
                    </div>
                  </TableCell>
                  <TableCell className={styles.cellTop}>
                    {item.submitter_name ?? 'Unknown requestor'}
                    {item.department && <div className={styles.sub}>{item.department}</div>}
                  </TableCell>
                  <TableCell className={mergeClasses(styles.cellTop, styles.num)}>
                    {fmtDate(item.routed_at) || '—'}
                  </TableCell>
                  <TableCell className={styles.cellTop}>
                    <span
                      className={mergeClasses(
                        styles.chip,
                        isNew(item) ? styles.chipNew : styles.chipWaiting,
                      )}
                      data-testid={`dc-status-${item.demand_id}`}
                    >
                      {statusLabel(item)}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}

export default DigitalCouncilQueue
